import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AdminSessionProvider } from './app/admin-session';
import { ConsoleRouter } from './app/router';
import { AppToaster } from './components/ui/app-toaster';
import { LocaleProvider } from './i18n/use-locale';
import { initErrorTracking, reportError } from './lib/error-tracking';
import { isApiError } from './lib/unwrap';
import './styles.css';

/**
 * One client for the process.
 *
 * The defaults are chosen for a console somebody keeps open all day: a screen
 * refetches when the tab comes back to the front, because the queue moved while
 * they were in their inbox, and a failed request is retried once rather than
 * three times — a 401 is already handled inside the API client, and everything
 * else that fails twice in a row is not going to work on the third try either.
 * A 4xx is not retried at all: it is the console's own request being refused,
 * and the same request again gets the same answer.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => failureCount < 1 && !(isApiError(error) && error.clientError),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  },
});

// Sentry, when a DSN was built in (VITE_SENTRY_DSN); nothing at all otherwise. Not
// awaited: the console renders while the SDK loads, and an error in those few
// milliseconds is still caught by the SDK's global handlers once it starts.
void initErrorTracking();

const container = document.querySelector('#root');
if (container === null) {
  throw new Error('index.html is missing #root.');
}

// React hands render errors to these rather than to `window.onerror`, so they are
// where Sentry hears about a screen that crashed. A caught error is one the router's
// error boundary rendered a fallback for; it is still logged, as React would by default.
createRoot(container, {
  onUncaughtError: (error) => {
    reportError(error);
  },
  onCaughtError: (error) => {
    reportError(error);
    console.error(error);
  },
}).render(
  <StrictMode>
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <AppToaster>
          <AdminSessionProvider>
            <ConsoleRouter />
          </AdminSessionProvider>
        </AppToaster>
      </QueryClientProvider>
    </LocaleProvider>
  </StrictMode>,
);
